import { GetCancerScreeningResultWithQueryModel, HpvTestResult, PathologyTestResult } from "../../models/getCancerScreeningResultModel";
import {
  Grid,
  Typography,
  lighten,
  makeStyles,
} from "@material-ui/core";
import { IHpvTestResultDetails, IPathologyTestResultDetails } from "../../interfaces/cancerScreeningRequest";
import React, { useContext } from "react";

import { DATE_FORMAT } from '../../constants';
import { LangContext } from "../../context/LangContext";
import moment from "moment";
import translations from "../../assets/json/translations.json";

interface IProps {
  screeningRequest: GetCancerScreeningResultWithQueryModel;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
  topItems: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.only("md")]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  titleDivider: {
    display: "inline-block",
    margin: theme.spacing(0, 1),
  },
  titleSerialNumber: {
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  otherDetails: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  descriptionTitle: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
    display: "inline-block",
  },
  descriptionDetails: {
    color: theme.palette.text.secondary,
    display: "inline-block",
  },
  resultsWrapper: {
    backgroundColor: lighten(theme.palette.secondary.light, 0.8),
    padding: theme.spacing(2),
  },
}));

const TestResults: React.FC<IProps> = ({
  screeningRequest,
}) => {
  const classes = useStyles({});
  const [lang] = useContext(LangContext);

  const hpvTestResults: IHpvTestResultDetails[] = 
  [
    {
      label: translations.sentToLab[lang],
      getVal: (hpvTestResult: HpvTestResult) =>
        hpvTestResult?.createdDate
          ? moment(hpvTestResult.createdDate).format(DATE_FORMAT)
          : "",
    },
    {
      label: translations.createdBy[lang],
      getVal: (hpvTestResult: HpvTestResult) =>
        !hpvTestResult ? "" : hpvTestResult?.hpvTestRequestedByName ? hpvTestResult?.hpvTestRequestedByName : translations.notRegistered[lang],
    },
    {
      label: translations.sampleReceived[lang],
      getVal: (hpvTestResult: HpvTestResult) =>
        hpvTestResult?.specimenArrivalDate && !moment(hpvTestResult.specimenArrivalDate).format(DATE_FORMAT).startsWith('01.01.0001')
          ? moment(hpvTestResult.specimenArrivalDate).format(DATE_FORMAT)
          : hpvTestResult?.specimenArrivalDate ? translations.dateNotReceived[lang] : "",
    },
    {
      label: translations.DateOfConclusion[lang],
      getVal: (hpvTestResult: HpvTestResult) =>
        hpvTestResult?.conclusionDate
          ? moment(hpvTestResult.conclusionDate).format(DATE_FORMAT)
          : "",
    },
    {
      label: translations.result[lang],
      render: (hpvTestResult: HpvTestResult) => (
        <>
          <Typography>{hpvTestResult?.conclusionNote}</Typography>
          <Grid container spacing={0}>
            {hpvTestResult?.codes?.map((item: any, index) => (
              <Grid item xs={12} key={index}>
                <Typography>{`${item.code} - ${item.status}`}</Typography>
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
  ];

  const pathologyTestResults: IPathologyTestResultDetails[] = 
  [
    {
      label: translations.sentToLab[lang],
      getVal: (pathologyTestResult: PathologyTestResult) =>
        pathologyTestResult?.createdDate
          ? moment(pathologyTestResult.createdDate).format(DATE_FORMAT)
          : "",
    },
    {
      label: translations.createdBy[lang],
      getVal: (pathologyTestResult: PathologyTestResult) =>
      !pathologyTestResult ? "" : pathologyTestResult?.pathologyTestRequestedByName ? pathologyTestResult?.pathologyTestRequestedByName : translations.notRegistered[lang],
    },
    {
      label: translations.sampleReceived[lang],
      getVal: (pathologyTestResult: PathologyTestResult) =>
      pathologyTestResult?.specimenArrivalDate && !moment(pathologyTestResult.specimenArrivalDate).format(DATE_FORMAT).startsWith('01.01.0001')
          ? moment(pathologyTestResult.specimenArrivalDate).format(DATE_FORMAT)
          : pathologyTestResult?.specimenArrivalDate ? translations.dateNotReceived[lang] : "",
    },
    {
      label: translations.DateOfConclusion[lang],
      getVal: (pathologyTestResult: PathologyTestResult) =>
        pathologyTestResult?.conclusionDate
          ? moment(pathologyTestResult.conclusionDate).format(DATE_FORMAT)
          : "",
    },
    {
      label: translations.result[lang],
      render: (pathologyTestResult: PathologyTestResult) => (
        <>
          <Typography component={"span"}>
            {pathologyTestResult?.conclusionNote}
          </Typography>
          <Grid container spacing={0}>
            {pathologyTestResult?.details?.map((item: any, index) => (
              <Grid item xs={12} key={index}>
                {`${item.code} - ${item.description}`}
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
  ];
  
  return (
   renderResults()
  );

  function renderResults() {
    return (
      <div className={classes.resultsWrapper}>
        <Grid container spacing={1} direction="row">
          <Grid item xs={12} sm={6}>
            {renderHpvResults()}
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderPathologyResults()}
          </Grid>
        </Grid>
      </div>
    );
  }

  function renderHpvResults() {
    return (
      <Grid container spacing={1} direction="row">
        <Grid item xs={12}>
          <Typography component={"span"} variant="h5">
            {translations.hpvResearch[lang]}
          </Typography>
        </Grid>
        {hpvTestResults.map((item: any, index) => (
          <Grid item xs={12} key={index}>
            {!(item.hide && item.hide(screeningRequest?.hpvTestResult)) && (
              <Typography
                component={"span"}
                style={{ position: "relative", display: "flex" }}
              >
                <div className={classes.descriptionTitle}>{item.label}:</div>
                <div className={classes.descriptionDetails}>
                  {item?.render && item.render(screeningRequest?.hpvTestResult)}
                  {item?.getVal && item.getVal(screeningRequest?.hpvTestResult)}
                </div>
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>
    );
  }

  function renderPathologyResults() {
    return (
      <Grid container spacing={1} direction="row">
        <Grid item xs={12}>
          <Typography component={"span"} variant="h5">
            {translations.cellularResearch[lang]}
          </Typography>
        </Grid>
        {pathologyTestResults.map((item: any, index) => (
          <Grid item xs={12} key={index}>
            {!(
              item.hide && item.hide(screeningRequest?.pathologyTestResult)
            ) && (
                <Typography
                  component={"span"}
                  style={{ position: "relative", display: "flex" }}
                >
                  <div className={classes.descriptionTitle}>{item.label}:</div>
                  <div className={classes.descriptionDetails}>
                    {item?.render &&
                      item.render(screeningRequest?.pathologyTestResult)}
                    {item?.getVal &&
                      item.getVal(screeningRequest?.pathologyTestResult)}
                  </div>
                </Typography>
              )}
          </Grid>
        ))}
      </Grid>
    );
  }
};

export default TestResults;
